import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Avatar,
  Layout,
  Menu,
  Badge,
  Row,
  Col,
  Drawer,
  Button,
  Grid,
  Typography,
} from "antd";
import {
  DollarOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  WechatOutlined,
  ShoppingOutlined,
  BellOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { useCart } from "../../contexts/CartContext";
import { useNotifications } from "../../contexts/NotificationContext";
import { useChat } from "../../contexts/ChatContext";

const { Header } = Layout;
const { useBreakpoint } = Grid;
const { Text } = Typography;

const MenuNavigation = () => {
  const location = useLocation();
  const screens = useBreakpoint();
  const [sideMenuOpen, setSideMenuOpen] = useState(false);

  const { carts } = useCart();
  const { unreadCount } = useNotifications();
  const { totalUnreadMessages } = useChat();

  const totalQuantity = carts.reduce(
    (acc, cart) =>
      acc + cart.items.reduce((sum, item) => sum + item.quantity, 0),
    0,
  );

  const getSelectedKey = () => {
    if (location.pathname.startsWith("/buy")) {
      return ["1", location.pathname === "/buy/order" ? "1-2" : "1-1"];
    }
    if (location.pathname.startsWith("/sell")) {
      return ["2", location.pathname === "/sell/order" ? "2-2" : "2-1"];
    }
    if (location.pathname.startsWith("/chat")) return ["3"];
    if (location.pathname.startsWith("/cart")) return ["4"];
    if (location.pathname.startsWith("/notification")) return ["5"];
    if (location.pathname.startsWith("/profile")) return ["6"];
    return [];
  };

  const selectedKeys = getSelectedKey();

  // 1. SINGLE SOURCE OF TRUTH: All 6 Navigation Items Defined Once
  const allNavItems = [
    {
      key: "1",
      icon: <ShoppingOutlined style={{ fontSize: "20px" }} />,
      label: "Buy",
      children: [
        { key: "1-1", label: <Link to="/buy/product">Browse Products</Link> },
        { key: "1-2", label: <Link to="/buy/order">My Purchases</Link> },
      ],
    },
    {
      key: "2",
      icon: <DollarOutlined style={{ fontSize: "20px" }} />,
      label: "Sell",
      children: [
        { key: "2-1", label: <Link to="/sell/product">My Products</Link> },
        { key: "2-2", label: <Link to="/sell/order">My Sales</Link> },
      ],
    },
    {
      key: "3",
      badgecount: totalUnreadMessages,
      icon: <WechatOutlined style={{ fontSize: "20px" }} />,
      label: "Chats",
      path: "/chat",
    },
    {
      key: "4",
      badgecount: totalQuantity,
      icon: <ShoppingCartOutlined style={{ fontSize: "22px" }} />,
      label: "My Cart",
      path: "/cart",
    },
    {
      key: "5",
      badgecount: unreadCount,
      icon: <BellOutlined style={{ fontSize: "22px" }} />,
      label: "Notifications",
      path: "/notification",
    },
    {
      key: "6",
      icon: <UserOutlined style={{ fontSize: "22px" }} />,
      label: "My Profile",
      path: "/profile",
    },
  ];

  // 2. DESKTOP CENTER MENU: First 3 items (Buy, Sell, Chats)
  const desktopMenuItems = allNavItems.slice(0, 3).map((item) => ({
    ...item,
    icon:
      item.badgecount !== undefined ? (
        <Badge count={item.badgecount} size="small" offset={[5, 0]}>
          {item.icon}
        </Badge>
      ) : (
        item.icon
      ),
    label: item.path ? <Link to={item.path}>{item.label}</Link> : item.label,
  }));

  // 3. DESKTOP ACTION ICONS: Last 3 items (Cart, Notifications, Profile)
  const desktopActionItems = allNavItems.slice(3);

  // 4. MOBILE DRAWER MENU: All 6 items mapped automatically
  const mobileMenuItems = allNavItems.map((item) => ({
    ...item,
    icon:
      item.badgecount !== undefined ? (
        <Badge count={item.badgecount} size="small" offset={[5, 0]}>
          {item.icon}
        </Badge>
      ) : (
        item.icon
      ),
    label: item.path ? <Link to={item.path}>{item.label}</Link> : item.label,
  }));

  return (
    <Header
      style={{
        background: "white",
        padding: "0 16px",
        borderBottom: "1px solid #f0f0f0",
      }}
    >
      <Row
        align="middle"
        justify="space-between"
        style={{ width: "100%", height: "100%" }}
      >
        {/* Logo Column */}
        <Col style={{ display: "flex", alignItems: "center" }}>
          <Link
            to="/buy/product"
            style={{ display: "flex", alignItems: "center" }}
          >
            <Avatar
              src="/logo1.png"
              alt="BazaarBuddy"
              size={44}
              shape="square"
            />
          </Link>
        </Col>

        {/* Desktop View (>= sm) */}
        {screens.sm ? (
          <>
            {/* Center Header Links */}
            <Col flex="1 1 auto" style={{ minWidth: 0, padding: "0 16px" }}>
              <Menu
                theme="light"
                mode="horizontal"
                selectedKeys={selectedKeys}
                items={desktopMenuItems}
                style={{ borderBottom: "none", justifyContent: "center" }}
              />
            </Col>

            {/* Top Right Action Icons */}
            <Col style={{ display: "flex", alignItems: "center", gap: "20px" }}>
              {desktopActionItems.map((action) => (
                <Link key={action.key} to={action.path}>
                  <Badge count={action.badgecount} size="small" offset={[2, 0]}>
                    {action.icon}
                  </Badge>
                </Link>
              ))}
            </Col>
          </>
        ) : (
          /* Mobile View (< sm) */
          <Col style={{ display: "flex", alignItems: "center" }}>
            <Button
              type="text"
              icon={<MenuOutlined style={{ fontSize: "22px" }} />}
              onClick={() => setSideMenuOpen(true)}
            />
          </Col>
        )}
      </Row>

      {/* Mobile Side Drawer */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Avatar
              src="/logo1.png"
              alt="BazaarBuddy"
              size={36}
              shape="square"
            />
            <Text strong style={{ fontSize: "16px" }}>
              BazaarBuddy
            </Text>
          </div>
        }
        placement="right"
        onClose={() => setSideMenuOpen(false)}
        open={sideMenuOpen}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          items={mobileMenuItems}
          onClick={() => setSideMenuOpen(false)}
        />
      </Drawer>
    </Header>
  );
};

export default MenuNavigation;
